import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from './LoadingSpinner';
import { trackLogout } from '../utils/metaPixel';

type LogoutDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const LogoutDialog: React.FC<LogoutDialogProps> = ({ isOpen, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [isProcessingLogout, setIsProcessingLogout] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) setIsProcessingLogout(false);
  }, [isOpen]);

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isProcessingLogout) onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, onClose, isProcessingLogout]);

  const handleConfirm = async () => {
    trackLogout();
    setIsProcessingLogout(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error('Error during logout:', error);
      setIsProcessingLogout(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-[2px] p-4"
          onClick={isProcessingLogout ? undefined : onClose}
          style={{ pointerEvents: 'all' }}
        >
          <motion.div
            initial={{ scale: 0.97, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.97, opacity: 0, y: 8 }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            className="ds-card-pop relative w-full max-w-[320px] p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-dialog-title"
          >
            {!isProcessingLogout && (
              <button
                type="button"
                onClick={onClose}
                className="absolute top-3.5 right-3.5 w-7 h-7 flex items-center justify-center rounded-md text-[var(--ds-text-muted)] hover:text-[var(--ds-text-strong)] hover:bg-[var(--ds-surface-hover)] transition-colors touch-manipulation"
                aria-label={t('common.close')}
              >
                <X className="w-4 h-4" />
              </button>
            )}

            <p id="logout-dialog-title" className="t-section pr-8">
              {t('dialog.logoutTitle')}
            </p>
            <p className="t-meta t-muted mt-2 leading-relaxed">
              {t('dialog.logoutMessage')}
            </p>

            <div className="flex flex-col-reverse gap-2 mt-5 sm:flex-row">
              <button
                type="button"
                onClick={onClose}
                disabled={isProcessingLogout}
                className="ds-btn ds-btn-secondary flex-1 min-h-[40px] touch-manipulation disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isProcessingLogout}
                className="ds-btn ds-btn-primary flex-1 min-h-[40px] touch-manipulation disabled:opacity-50"
              >
                {isProcessingLogout ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <LoadingSpinner size="xs" showText={false} />
                    {t('dialog.loggingOut')}
                  </span>
                ) : (
                  t('dialog.logoutConfirm')
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default React.memo(LogoutDialog);
